import { useFormContext } from "react-hook-form";
import type { Components } from "react-markdown";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { EditableMarkdown } from "components/form_components/EditableMarkdown";
import { FileInput } from "components/form_components/FileInput";
import { Checkbox, Input } from "components/form_components/Inputs";
import { Markdown } from "components/reports/builder_components/Markdown";
import Alert from "components/ui/alert/Alert";
import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { useCreateAiToneOfVoice } from "lib/hooks/api/ai_tone_of_voice/useCreateAiToneOfVoice";
import { useUpdateAiToneOfVoice } from "lib/hooks/api/ai_tone_of_voice/useUpdateAiToneOfVoice";
import { FileWithDirectoryPath } from "lib/hooks/api/customer_documents/types";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventNames, useTracking } from "lib/tracking";
import { AiToneOfVoice } from "../../../lib/hooks/api/ai_tone_of_voice/useAiToneOfVoices";
import { LoadingState } from "../../app_layout/LoadingState";
import { ToneOfVoiceProcessingState } from "./useToneOfVoiceDocumentProcessing";
import { ToneOfVoiceFormValues, ViewState } from "./utils";

const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.odt,.txt";

// AI-generated tone-of-voice summaries often include large ATX headings
// (#, ##, ###). Downscale them so they fit within the capped-height
// description field and match the surrounding modal typography.
const HeadingH1 = styled.h1({ fontSize: "18px !important" });
const HeadingH2 = styled.h2({ fontSize: "16px !important" });
const HeadingH3 = styled.h3({ fontSize: "14px !important" });
const Paragraph = styled.p({ marginTop: "0px !important" });

const markdownStyleComponents: Components = {
  h1: ({ children }) => <HeadingH1>{children}</HeadingH1>,
  h2: ({ children }) => <HeadingH2>{children}</HeadingH2>,
  h3: ({ children }) => <HeadingH3>{children}</HeadingH3>,
  p: ({ children }) => <Paragraph>{children}</Paragraph>,
};

type Props = {
  viewState: ViewState;
  processingState: ToneOfVoiceProcessingState;
  existingTovCount: number;
  entry?: AiToneOfVoice;
  mode: "create" | "edit";
  selectedFiles: FileWithDirectoryPath[];
  closeModal: () => void;
  startUpload: (file: File) => Promise<void>;
  setManualMode: (isManual: boolean) => void;
  setSelectedFiles: (files: FileWithDirectoryPath[]) => void;
  resetProcessing: () => void;
};

export default function ToneOfVoiceModalContent({
  entry,
  mode,
  viewState,
  processingState,
  existingTovCount,
  selectedFiles,
  startUpload,
  closeModal,
  setManualMode,
  setSelectedFiles,
  resetProcessing,
}: Props): JSX.Element {
  const { logEvent } = useTracking();
  const { control, formState, handleSubmit } = useFormContext<ToneOfVoiceFormValues>();

  const { mutate: createToneOfVoice, isPending: isCreating } = useCreateAiToneOfVoice({
    onSuccess: (data) => {
      const response = data.createAiToneOfVoice;
      logEvent(EventNames.toneOfVoiceGuideCreated, {
        "Label character count": response.label.length,
        "Value character count": response.rawValue.length,
        "Total tone of voice configs": existingTovCount + 1,
        "Context source": response.sourceDocumentId ? "Document" : "Manual",
      });
      toast.success("Tone of voice created");
      closeModal();
    },
    onError: () => {
      toast.error("Failed to create tone of voice");
    },
  });

  const { mutate: updateToneOfVoice, isPending: isUpdating } = useUpdateAiToneOfVoice({
    onSuccess: (data) => {
      const response = data.updateAiToneOfVoice;
      logEvent(EventNames.toneOfVoiceGuideEdited, {
        "Context source": response.sourceDocumentId ? "Document" : "Manual",
      });
      toast.success("Tone of voice updated");
      closeModal();
    },
    onError: () => {
      toast.error("Failed to update tone of voice");
    },
  });

  const isPending = mode === "create" ? isCreating : isUpdating;

  const handleCancelProcessing = () => {
    resetProcessing();
    setSelectedFiles([]);
  };

  const handleFilesChange = (files: FileWithDirectoryPath[]) => {
    setSelectedFiles(files);
    if (files.length > 0) {
      void startUpload(files[0]);
    }
  };

  const onSubmit = (data: ToneOfVoiceFormValues) => {
    if (mode === "edit" && entry) {
      updateToneOfVoice({ input: { id: entry.id, ...data } });
      return;
    }
    createToneOfVoice({
      input: {
        ...data,
        sourceDocumentId:
          processingState.status === "success" ? processingState.sourceDocumentId : undefined,
      },
    });
  };

  const processingError = processingState.status === "error" ? processingState.error : undefined;
  const isUploading = processingState.status === "uploading";
  const isStotlesDefault = mode === "edit" && !entry?.companyId;
  const disableEdit = isPending || isStotlesDefault;

  if (viewState === "upload") {
    return (
      <Flex column gap={16}>
        {processingError && (
          <Alert type="error">
            {processingError} Please try again or write your tone of voice manually.
          </Alert>
        )}

        <FileInput
          onChange={handleFilesChange}
          value={selectedFiles}
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          multiple={false}
          title="Upload a style guide or brand document"
          listFileNamesInline={false}
        />

        {isUploading && (
          <Flex alignItems="center" justifyContent="center" gap={8}>
            <UIcon icon="spinner" size={20} />
            <Text color="sysTextSecondary">Uploading document...</Text>
          </Flex>
        )}

        <Flex justifyContent="center" alignItems="center">
          <Button
            buttonType="secondary"
            icon="pencil02"
            onClick={() => setManualMode(true)}
            fullWidth
          >
            Write your tone of voice instead
          </Button>
        </Flex>
      </Flex>
    );
  }

  if (viewState === "manual") {
    return (
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Input
          name="label"
          control={control}
          label="Tone of voice name"
          required
          placeholder="Enter a tone of voice name"
          disabled={disableEdit}
        />

        <div aria-label="Tone of voice description editor">
          <EditableMarkdown
            name="rawValue"
            control={control}
            label="Description"
            required
            placeholder="Enter tone of voice description"
            disabled={disableEdit}
            autoSize={{ minRows: 15, maxRows: 15 }}
            maxHeight={360}
            minHeight={360}
            bordered
            customComponents={markdownStyleComponents}
          />
        </div>

        <Flex justifyContent="space-between" alignItems="center" gap={8}>
          <Flex column gap={4}>
            <Checkbox
              name="isDefault"
              control={control}
              label=""
              fieldLabel="Set as default"
              disabled={disableEdit}
            />
            {entry?.isDefault && (
              <Text color="sysTextSecondary">This is currently your default</Text>
            )}
          </Flex>
          <ButtonRow gap={8} alignItems="center">
            <Button buttonType="secondary" onClick={closeModal} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={disableEdit || !formState.isValid} loading={isPending}>
              {mode === "create" ? "Create" : "Save changes"}
            </Button>
          </ButtonRow>
        </Flex>
      </Form>
    );
  }

  if (viewState === "processing") {
    return (
      <Flex column gap={24} alignItems="center" style={{ padding: "32px 0" }}>
        <LoadingState
          title="Analysing your document..."
          description="Your document is being read and summarised into a tone of voice guide for Stotles AI"
          width="500px"
          actionNode={
            <Button buttonType="secondary" onClick={handleCancelProcessing}>
              Cancel
            </Button>
          }
        />
      </Flex>
    );
  }

  if (viewState === "review") {
    const isStreaming = processingState.status === "streaming";
    const streamedRawValue = isStreaming ? processingState.streamedRawValue : "";

    return (
      <Form onSubmit={handleSubmit(onSubmit)}>
        <AiGeneratedBadge alignItems="center" gap={6}>
          <UIcon icon="stars2" size={16} />
          <Text body color="sysTextSecondary">
            AI-generated from your document. You can edit before saving.
          </Text>
        </AiGeneratedBadge>

        <Input
          name="label"
          control={control}
          label="Tone of voice name"
          required
          placeholder="Enter a tone of voice name"
          disabled={isStreaming}
        />

        {isStreaming ? (
          <Flex column aria-label="Streaming tone of voice description">
            <StreamingLabel>
              <span>*</span>Description
            </StreamingLabel>
            <StreamingMarkdownContainer>
              {streamedRawValue ? (
                <Markdown
                  text={streamedRawValue}
                  targetType="new-tab"
                  customComponents={markdownStyleComponents}
                />
              ) : (
                <Text color="sysTextSecondary">Generating tone of voice...</Text>
              )}
            </StreamingMarkdownContainer>
          </Flex>
        ) : (
          <div aria-label="Tone of voice description editor">
            <EditableMarkdown
              // Remount once the AI result has seeded the form so
              // EditableMarkdownText picks up the new initialValue. For manual
              // flows (no seeding) the key is stable.
              key={
                processingState.status === "success"
                  ? `seeded:${processingState.sourceDocumentId}`
                  : "manual"
              }
              name="rawValue"
              control={control}
              label="Description"
              required
              placeholder="Enter tone of voice description"
              autoSize={{ minRows: 15, maxRows: 15 }}
              maxHeight={360}
              minHeight={360}
              bordered
              customComponents={markdownStyleComponents}
            />
          </div>
        )}

        <Flex justifyContent="space-between" alignItems="center" gap={8}>
          <Checkbox
            name="isDefault"
            control={control}
            label=""
            fieldLabel="Set as default"
            disabled={isStreaming}
          />
          <ButtonRow gap={8} alignItems="center">
            <Button
              buttonType="secondary"
              onClick={closeModal}
              disabled={isCreating || isStreaming}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isStreaming || !formState.isValid} loading={isCreating}>
              Create
            </Button>
          </ButtonRow>
        </Flex>
      </Form>
    );
  }

  // Shouldn't reach here, but TS needs a fallback
  return <></>;
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const ButtonRow = styled(Flex)({
  alignItems: "center",
  gap: 8,
  marginTop: 8,
});

const AiGeneratedBadge = styled(Flex)(({ theme }) => ({
  padding: "8px 12px",
  borderRadius: 4,
  backgroundColor: theme.colors.sysBackgroundAlternative,
}));

const StreamingLabel = styled.label(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  marginBottom: 4,
  "& > span": {
    color: theme.colors.sysDestructiveDefault,
    marginRight: 4,
  },
}));

const StreamingMarkdownContainer = styled.div(({ theme }) => ({
  minHeight: 120,
  // Roughly matches the editable textarea's maxRows cap so the streaming
  // content is clipped to the input area and scrolls internally rather
  // than growing the modal past the viewport.
  maxHeight: 320,
  overflowY: "auto",
  padding: 8,
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundAlternative,
}));
